import { Injectable, Optional } from "@angular/core";
import { Logger } from "../../logger";
import {
  ContentAnalysisResult,
  ContentUploadStatus,
  CropSettings,
  MediaInfo,
  MediaItem,
  MediaLinkedType,
  MediaSegment,
  MediaType,
} from "../../../domain/media";
import moment from "moment";
import { Subtitle } from "../../../domain/subtitle";
import { RectangleF } from "../../../domain/rectangle-f";
import { Tag } from "../../../domain/tag";

function toContentMediaType(mediaType: string | number | undefined): MediaType | undefined {
  if (mediaType === undefined || mediaType === null) return undefined;
  // API may return numeric enum (0=AudioVideo, 1=Video, 2=Audio, 3=Image, etc.)
  if (typeof mediaType === "number") {
    return mediaType in MediaType ? (mediaType as MediaType) : undefined;
  }
  const mediaTypes: Record<string, MediaType> = {
    AudioVideo: MediaType.AudioVideo,
    Video: MediaType.Video,
    Audio: MediaType.Audio,
    Image: MediaType.Image,
    FlexAnimation: MediaType.FlexAnimation,
    Subtitles: MediaType.Subtitles,
    Unknown: MediaType.Unknown,
    Contract: MediaType.Contract,
  };
  return mediaTypes[mediaType];
}

function toMediaLinkedType(mediaLinkedType) {
  const mediaLinkedTypes = {
    Trailer: MediaLinkedType.Trailer,
    Audio: MediaLinkedType.Audio,
    Contract: MediaLinkedType.Contract,
  };

  return mediaLinkedTypes[mediaLinkedType];
}

function toContentUploadStatus(uploadStatus) {
  switch (uploadStatus) {
    case "Enabled":
      return ContentUploadStatus.Enabled;
    case "Disabled":
      return ContentUploadStatus.Disabled;
    case "Pending":
      return ContentUploadStatus.Pending;
  }
}

function toContentAnalysisResult(contentAnalysisResult) {
  const analysisResults = {
    OK: ContentAnalysisResult.Ok,
    DownloadFailed: ContentAnalysisResult.DownloadFailed,
    UnknownMediaType: ContentAnalysisResult.UnknownMediaType,
    ServerFailure: ContentAnalysisResult.ServerFailure,
  };

  return analysisResults[contentAnalysisResult];
}

function toMedia(media) {
  const item = new MediaItem(
    media.MediaId,
    media.MediaGuid,
    media.MediaName,
    media.UserFileName,
    toContentMediaType(media.ContentMediaType),
    moment.duration(media.ContentDuration, "milliseconds"),
    media.ContentSnapshotUrl,
    media.FpsRate,
    toContentUploadStatus(media.ContentUploadStatus),
    media.ParentMediaId || null,
    toMediaLinkedType(media.MediaLinkedType),
    media.TenantMediaUniqueId,
    media.Quality,
    media.IsRecord,
  );
  const uploadedTime = media.UploadedTime ?? media.uploadedTime;
  if (uploadedTime != null) {
    item.uploadedTimeMs =
      typeof uploadedTime === "number"
        ? uploadedTime
        : new Date(uploadedTime).getTime();
  }
  return item;
}

function toMediaInfo(mediaInfo) {
  let floatingMediaPosition = new RectangleF(0, 0, 1, 1);
  if (mediaInfo.FloatingMediaPosition) {
    floatingMediaPosition = new RectangleF(
      mediaInfo.FloatingMediaPosition.x,
      mediaInfo.FloatingMediaPosition.y,
      mediaInfo.FloatingMediaPosition.width,
      mediaInfo.FloatingMediaPosition.height,
    );
  }

  function getTagsFromResponse(mediaTagsResponse: any) {
    const result = [];
    if (!mediaTagsResponse) {
      return result;
    }
    mediaTagsResponse.forEach((r) => {
      if (r.TagGroupName && r.TagGroupName === "GENERAL") {
        r.Tags.forEach((responseTag) => {
          result.push(
            new Tag(
              responseTag.IsGeneral,
              responseTag.TagId,
              responseTag.TagName,
            ),
          );
        });
      }
    });

    return result;
  }

  function getAssetTypesFromResponse(mediaTagsResponse: any) {
    const result = [];
    if (!mediaTagsResponse) {
      return result;
    }
    mediaTagsResponse.forEach((r) => {
      if (r.TagGroupName && r.TagGroupName === "ASSET TYPE") {
        r.Tags.forEach((responseTag) => {
          result.push(
            new Tag(
              responseTag.IsGeneral,
              responseTag.TagId,
              responseTag.TagName,
            ),
          );
        });
      }
    });

    return result;
  }

  function getDayPartingFromResponse(mediaTagsResponse: any) {
    const result = [];
    if (!mediaTagsResponse) {
      return result;
    }
    mediaTagsResponse.forEach((r) => {
      if (r.TagGroupName && r.TagGroupName === "DAYPARTING") {
        r.Tags.forEach((responseTag) => {
          result.push(
            new Tag(
              responseTag.IsGeneral,
              responseTag.TagId,
              responseTag.TagName,
            ),
          );
        });
      }
    });

    return result;
  }

  const result = new MediaInfo(
    mediaInfo.MediaId,
    mediaInfo.MediaGuid,
    mediaInfo.MediaName,
    mediaInfo.UserFileName,
    toContentMediaType(mediaInfo.ContentMediaType),
    moment.duration(mediaInfo.ContentDuration, "milliseconds"),
    mediaInfo.ContentSnapshotUrl,
    toContentUploadStatus(mediaInfo.ContentUploadStatus),
    mediaInfo.ParentMediaId || null,
    toMediaLinkedType(mediaInfo.MediaLinkedType),
    mediaInfo.ContentFps,
    mediaInfo.UploadTime,
    mediaInfo.ContentHeight,
    mediaInfo.ContentWidth,
    mediaInfo.MediaDescription,
    mediaInfo.MediaLicenseFrom ? moment(mediaInfo.MediaLicenseFrom) : null,
    mediaInfo.MediaLicenseTo ? moment(mediaInfo.MediaLicenseTo) : null,
    mediaInfo.ContentHasAlpha,
    toContentAnalysisResult(mediaInfo.ContentAnalysisResult),
    mediaInfo.UploaderUserId,
    moment(mediaInfo.UploadTime),
    moment(mediaInfo.AnalyzedTime),
    mediaInfo.IsUploadedToIChannelStorage,
    getTagsFromResponse(mediaInfo.MediaTagsList),
    getAssetTypesFromResponse(mediaInfo.MediaTagsList),
    getDayPartingFromResponse(mediaInfo.MediaTagsList),
    (mediaInfo.MediaLicenseCountries || []).map((country) => country.CountryId),
    mediaInfo.TenantMediaUniqueId,
    (mediaInfo.Subtitles || []).map((subtitle) => {
      return new Subtitle(
        subtitle.id,
        subtitle.language,
        subtitle.offset,
        subtitle.fps === null ? -1 : subtitle.fps,
        subtitle.playbackSpeed,
        subtitle.url,
      );
    }),
    (mediaInfo.LinkedFiles || [])
      .filter((x) => x.MediaLinkedType === "Audio")
      .map((audioFile) => {
        return {
          id: audioFile.MediaId,
          name: audioFile.MediaName || audioFile.UserFileName,
          contentUploadStatus: toContentUploadStatus(
            audioFile.ContentUploadStatus,
          ),
          language: audioFile.Language ? audioFile.Language : null,
        };
      }),
    mediaInfo.PackshotIsAnalyzed,
    (mediaInfo.LinkedFiles || [])
      .filter((x) => x.MediaLinkedType === "Trailer")
      .map((trailer) => {
        return {
          id: trailer.MediaId,
          thumbnailUrl: trailer.ContentSnapshotUrl,
          contentUploadStatus: toContentUploadStatus(
            trailer.ContentUploadStatus,
          ),
          name: trailer.MediaName,
        };
      }),
    (mediaInfo.Metadata || []).map((metadata) => {
      return { id: metadata.Id, value: metadata.Value };
    }),
    mediaInfo.FrameFormat,
    mediaInfo.OriginalMediaIsDeleted,
    mediaInfo.ContentSampleRate,
    mediaInfo.ContentChannelCount,
    mediaInfo.GeneralVolume,
    mediaInfo.TranscodeVideoUrl,
    mediaInfo.DuringRecording,
    mediaInfo.ContentUrl,
    toCropSettings(JSON.parse(mediaInfo.CropSettingsSerialized)),
    mediaInfo.ContentPixelAspectHeight,
    mediaInfo.ContentPixelAspectWidth,
    mediaInfo.StartTimestampMs,
    [],
    mediaInfo.FloatingMediaDepth,
    mediaInfo.TranscodedAlternateUrls,
    mediaInfo.MediaType,
    mediaInfo.IsRecord,
    floatingMediaPosition,
    mediaInfo.Lufs,
    mediaInfo.MediaQCWorkflowObjectives,
    mediaInfo.MediaShouldSqueeze,
    (mediaInfo.ClosedCaptions || []).map((cc) => {
      return new Subtitle(
        cc.id,
        cc.language,
        cc.offset,
        cc.fps === null ? -1 : cc.fps,
        cc.playbackSpeed,
        cc.url,
        cc.originalContentUrl,
        cc.flavor,
      );
    }),
  );
  if (mediaInfo.Segments) {
    result.segments.push(
      ...mediaInfo.Segments.map(
        (s) =>
          new MediaSegment(
            result,
            s.TenantUniqueId,
            s.Uid,
            s.Description,
            s.TimeInMs,
            s.TimeOutMs,
          ),
      ),
    );
  }
  return result;
}

function toCropSettings(cropSettingsRequest): CropSettings {
  let returnValue = CropSettings.createNoCropSettings();

  if (cropSettingsRequest) {
    // tslint:disable-next-line:max-line-length
    returnValue = new CropSettings(
      cropSettingsRequest.Top * 100,
      cropSettingsRequest.Bottom * 100,
      cropSettingsRequest.Left * 100,
      cropSettingsRequest.Right * 100,
    );
  }
  return returnValue;
}

@Injectable()
export class MediaMapper {
  constructor(@Optional() private logger: Logger) {}

  mapGetAllMediaResponse(allMedia: any): Promise<MediaItem[]> {
    const medias = !!allMedia && allMedia.length > 0 ? allMedia : [];
    return Promise.resolve(medias.map((x) => toMedia(x)));
  }

  mapGetMediaInfoResponse(response: any): Promise<MediaInfo> {
    return Promise.resolve(toMediaInfo(response));
  }

  mapUpdateMediaInfoRequest(media: MediaInfo) {
    let floatingMediaPositionRequest = null;
    if (!!media.floatingMediaPosition) {
      floatingMediaPositionRequest = {
        X: media.floatingMediaPosition.x,
        Y: media.floatingMediaPosition.y,
        Width: media.floatingMediaPosition.width,
        Height: media.floatingMediaPosition.height,
        Depth: media.floatingMediaDepth,
      };
    }
    const allMediaTagIds = media.tags
      .concat(media.dayParting, media.assetTypes)
      .map((r) => r.id);
    return {
      MediaID: media.id,
      MediaUID: media.uid,
      MediaName: media.name,
      MediaDescription: media.description,
      ContentSnapshotUrl: media.contentSnapshotUrl,
      MediaLicenseFrom: media.licenseFrom
        ? media.licenseFrom.format("YYYYMMDD")
        : null,
      MediaLicenseTo: media.licenseTo
        ? media.licenseTo.format("YYYYMMDD")
        : null,
      MediaTags: allMediaTagIds.join(","),
      Countries: media.mediaLicenseCountryIds.join(","),
      TenantMediaUniqueId: media.tenantMediaId || null,
      Metadata: media.metadata.map((metadata) => {
        return { Id: metadata.id, Value: metadata.value };
      }),
      AudioFilesLanguages: media.audioFiles.map((audioFile) => {
        return { mediaId: audioFile.id, languageCode: audioFile.language };
      }),
      SubtitleFilesLanguages: media.subtitles.map((subtitle) => {
        return {
          Id: subtitle.id,
          LanguageCode: subtitle.language,
          Fps: subtitle.fps === -1 ? null : subtitle.fps,
        };
      }),
      ClosedCaptionFilesLanguages: media.closedCaptions.map((cc) => {
        return {
          Id: cc.id,
          LanguageCode: cc.language,
          Fps: cc.fps === -1 ? null : cc.fps,
        };
      }),
      FrameFormat: media.frameFormat,
      Duration: media.contentDuration.asMilliseconds(),
      CropSettings:
        media.cropSettings === null
          ? null
          : media.cropSettings.toCropSettingsRequest(),
      StartTimestampMs: media.startTimeStampMs,
      Segments: media.segments.map((s) => {
        return {
          MediaId: s.parentMedia.id,
          TenantUniqueId: s.tenantUniqueId,
          Uid: s.uid,
          Description: s.description,
          TimeInMs: s.timeInMs,
          TimeOutMs: s.timeOutMs,
        };
      }),
      FloatingMediaPosition: floatingMediaPositionRequest,
      ParentMediaId: media.parentMediaId,
      MediaQCWorkflowObjectives: media.mediaQCWorkflowObjectives,
      MediaShouldSqueeze: media.mediaShouldSqueeze,
    };
  }
}
